import { LoadingIndicator } from '@xolv-mybrightlink/common';
import { Box, Typography } from '@mui/material';
import { useProgressPortraitContext } from './ProgressPortraitProvider';
import { GoalProgressMetric } from './GoalProgressMetric';
import { DevelopmentMilestoneMetric } from './DevelopmentMilestoneMetric';
import { ProblemBehaviorMetric } from './ProblemBehaviorMetric';
import { DangerousBehaviorMetric } from './DangerousBehaviorMetric';

import { ProgressPortraitMetrics } from '../model/ProgressPortrait';
import { ParentConfidenceMetric } from './ParentConfidenceMetric';
import { WellbeingScaleMetric } from './WellbeingScaleMetric';
import { TreatmentImpactMetric } from './TreatmentImpactMetric';
import { ParentStressMetric } from './ParentStressMetric';
import { formatDateMMddYYYY } from '../utils/utils';
import { ReactNode } from 'react';
import { isEmpty } from 'lodash';

export const ProgressPortraitTimeline = () => {
  const { store } = useProgressPortraitContext();
  const selectedAbaCaseId = store.getSelectedAbaCaseSfId(
    store.selectedPatientSfId,
  );
  if (!selectedAbaCaseId) {
    return null;
  }
  const progressPortraitData = store.getProgressPortraitData(
    store.selectedPatientSfId,
    selectedAbaCaseId,
  );
  if (!progressPortraitData) {
    return null;
  }
  if (
    progressPortraitData === 'not-load' ||
    progressPortraitData === 'loading'
  ) {
    return <LoadingIndicator />;
  }
  if (progressPortraitData === 'error') {
    return null;
  }
  const baselineMetric = progressPortraitData.baselineMetrics;
  const timelineMetrics = progressPortraitData.portraitTimelineMetrics;
  const periods = getPeriod(timelineMetrics);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <Box sx={{ height: 50, alignContent: 'flex-start' }}>
          {baselineMetric.vinelandAssessmentDate && (
            <Typography fontSize={14} align='center' fontWeight={500}>
              {formatDateMMddYYYY(baselineMetric.vinelandAssessmentDate)}
            </Typography>
          )}
          <Typography fontSize={14} align='center' fontWeight={500}>
            (baseline)
          </Typography>
        </Box>
        <Box>
          <MetricTitle metricTitle='Goal Progress' />
          <GoalProgressMetric progressPortraitMetrics={[baselineMetric]} />
        </Box>
        <Box>
          <MetricTitle metricTitle='Developmental Milestones' />
          <DevelopmentMilestoneMetric
            progressPortraitMetrics={[baselineMetric]}
          />
        </Box>
        <Box>
          <MetricTitle metricTitle='Problem Behaviors' />
          <ProblemBehaviorMetric progressPortraitMetrics={[baselineMetric]} />
        </Box>
        <Box>
          <MetricTitle metricTitle='Dangerous Behaviors' />
          <DangerousBehaviorMetric
            progressPortraitMetrics={[baselineMetric]}
            isBaseline
          />
        </Box>
        <Box>
          <MetricTitle metricTitle='Parent Stress' />
          <ParentStressMetric progressPortraitMetrics={[baselineMetric]} />
        </Box>
        <Box>
          <MetricTitle metricTitle='Parent Confidence' />
          <ParentConfidenceMetric progressPortraitMetrics={[baselineMetric]} />
        </Box>
        <Box>
          <MetricTitle metricTitle='Wellbeing Scale' />
          <WellbeingScaleMetric progressPortraitMetrics={[baselineMetric]} />
        </Box>
        <Box>
          <MetricTitle metricTitle='Treatment Impact Indicator' />
          <TreatmentImpactMetric progressPortraitMetrics={[baselineMetric]} />
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
        }}
      >
        {isEmpty(timelineMetrics) ? (
          <EmptyTimeline />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <Box sx={{ display: 'flex', height: 50 }}>
              {periods.map((period) => (
                <Box key={period} sx={{ flex: 1 }}>
                  <Typography fontSize={14} fontWeight={500} align='center'>
                    {period}
                  </Typography>
                </Box>
              ))}
            </Box>
            <ProgressRow>
              <GoalProgressMetric progressPortraitMetrics={timelineMetrics} />
            </ProgressRow>
            <ProgressRow>
              <DevelopmentMilestoneMetric
                progressPortraitMetrics={timelineMetrics}
              />
            </ProgressRow>
            <ProgressRow>
              <ProblemBehaviorMetric
                progressPortraitMetrics={timelineMetrics}
              />
            </ProgressRow>
            <ProgressRow>
              <DangerousBehaviorMetric
                progressPortraitMetrics={timelineMetrics}
                isBaseline={false}
              />
            </ProgressRow>
            <ProgressRow>
              <ParentStressMetric progressPortraitMetrics={timelineMetrics} />
            </ProgressRow>
            <ProgressRow>
              <ParentConfidenceMetric
                progressPortraitMetrics={timelineMetrics}
              />
            </ProgressRow>
            <ProgressRow>
              <WellbeingScaleMetric progressPortraitMetrics={timelineMetrics} />
            </ProgressRow>
            <ProgressRow>
              <TreatmentImpactMetric
                progressPortraitMetrics={timelineMetrics}
              />
            </ProgressRow>
          </Box>
        )}
      </Box>
    </Box>
  );
};

type MetricTitleProps = {
  metricTitle: string;
};
const MetricTitle = ({ metricTitle }: MetricTitleProps) => {
  return (
    <Typography fontSize={18} mb={2} fontWeight={500}>
      {metricTitle}
    </Typography>
  );
};

type ProgressRowProps = {
  children: ReactNode;
};

const ProgressRow = ({ children }: ProgressRowProps) => {
  return (
    <Box height={72} sx={{ alignContent: 'flex-end' }}>
      {children}
    </Box>
  );
};

const EmptyTimeline = () => {
  return (
    <Box
      sx={{
        m: 'auto',
        width: 400,
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
      }}
    >
      <Typography align='center'>
        No Progress Portrait available because your treatment is less than 6
        months.
      </Typography>
    </Box>
  );
};

const getPeriod = (progressPortraitMetrics: ProgressPortraitMetrics[]) => {
  return progressPortraitMetrics.map((metric) =>
    formatDateMMddYYYY(metric.vinelandAssessmentDate),
  );
};
